<!-- src/components/BetSlip.vue -->
<template>
    <!-- My BetSlip -->
    <div id="BetSlipContent" class="betslip-content">
      <div class="betslip-container">
        <!-- Header -->
        <div class="betslip-header-wrap">
          <div class="text-l">
            <span id="betslip-number">{{ betSlipStore.betCount }}</span>
            <span class="betsliptext">Betslip</span>
          </div>

          <div class="text" id="removeAllBets" @click="onRemoveAll">
            Remove All <i class="bi bi-trash"></i>
          </div>

          <!-- Close button: visible only on mobile devices -->
          <div class="text-r d-md-none" @click="onClose">
            <div class="text">close</div>
            <button type="button">
              <span aria-hidden="true"><i class="bi bi-x"></i></span>
            </button>
          </div>
        </div>

        <!-- Empty state -->
        <div id="NoBetSelected" class="no-betslip" v-if="!betSlipStore.hasBets">
          <div class="betslip-selection-none">
            <img :src="icons.nobetsFound" alt="no betslip" />
            <div class="title">No selections</div>
            <div class="text">
              Please add match selections by selecting the odds on any match. If you have a booking
              Code enter below
            </div>
          </div>

          <div class="enter-bookingcode">
            <input
              type="text"
              id="bookingCode"
              class="form-control"
              placeholder="Enter Booking Code"
              v-model="bookingCodeInput"
              @keydown.enter.prevent="handleLoadBookingCode"
              :disabled="loadingBookingCode"
            />
            <button class="btn" @click="handleLoadBookingCode" :disabled="loadingBookingCode">
              <span v-if="loadingBookingCode" class="spinner-border spinner-border-sm"></span>
              <span v-else>Load Betslip</span>
            </button>
          </div>
        </div>

        <!-- Bets list -->
        <div id="betSlipDetails" v-else>
          <!-- Boost strip -->
          <div class="multibet_boost" v-if="boostPercent > 0 && allOddsAbove13">
            <div class="text_icon">
              <img :src="icons.boosted" alt="Boosted Icon" />
            </div>

            <div class="text_below">
              <div class="text-top">
                Your Multibet of {{ betSlipStore.betCount }} selections gives you a boost of
                <span class="bold_text"
                  >{{ boostPercent }}% ({{ currency }} {{ fmt(boostAmount) }})</span
                >
              </div>
              <div class="text_r" v-if="nextBoost">
                <span class="boosted_text"
                  >Add {{ nextBoost.nextLegsNeeded }} more to get a {{ nextBoost.nextBonus }}%
                  boost!</span
                >
                <span class="boosted_min">1.3 minimum odds per game</span>
              </div>
            </div>
          </div>

          <!-- Items -->
          <div class="betslip-wrapper">
            <div
              class="betslip"
              v-for="bet in betSlipStore.bets"
              :key="bet.outcome_id"
              :class="{ 'highlight-invalid': bet.match_id === errorMatchId || isExpired(bet) }"
            >
              <div class="playing-teams">
                <span class="remove-bet">
                  <a href="#!" @click.prevent="onRemove(bet.outcome_id)"><i class="bi bi-x"></i></a>
                </span>

                <div class="match_details">
                  <span class="playing-teams-name">{{ bet.match_name }}</span>
                  <div class="match-markets-wrap">
                    <span class="match-markets-type">{{ bet.market_name }}</span>
                    <span class="match-final-outcome">{{ bet.outcome_name }}</span>
                  </div>
                  <span class="time-and-date">{{ formatDate(bet.date) }}</span>
                </div>

                <div class="mathchods">{{ parseFloat(bet.odds).toFixed(2) }}</div>
              </div>

              <!-- Remove expired bet (highest priority) -->
              <div
                v-if="isExpired(bet)"
                id="removeGame"
                class="deactivated-game"
                @click="onRemove(bet.outcome_id)"
              >
                Remove expired Markets
              </div>

              <!-- Per-bet Odds Changed indicator (only if not expired) -->
              <div
                v-else-if="betSlipStore.acceptOddsChanges && bet.oddsChanged"
                id="activatedGame"
                class="activated-game"
              >
                Odds Changed
              </div>

              <!-- Highlight note for invalid bet -->
              <div
                v-if="bet.match_id === errorMatchId"
                class="invalid-note"
                @click="onRemove(bet.outcome_id)"
              >
                This bet has an issue. Click to remove it.
              </div>
            </div>
          </div>
        </div>

        <!-- stake-container Modal -->
        <div class="stake-container" v-if="betSlipStore.hasBets">

          <div class="placebet__details__wrapper">
            <!-- Accept odds + booking code -->
            <div class="accept-terms-wrap">
              <div class="accept-terms">
                <input
                  id="accept-Terms-and-Conditions"
                  type="checkbox"
                  v-model="betSlipStore.acceptOddsChanges"
                />
                <label for="accept-Terms-and-Conditions">Accept Odds Changes</label>
              </div>

              <div class="booking-code-input">
                <div class="code-text">Booking Code</div>
                <code class="code-number">{{
                  betSlipStore.bookingCode ? `#${betSlipStore.bookingCode}` : '#N/A'
                }}</code>
              </div>
            </div>

            <!-- Total odds -->
            <div class="totalslip">
              <div class="totalslip-wrap">
                <div class="totalslip-text">Total Odds</div>
                <div class="totalslip-no">{{ betSlipStore.totalOdds }}</div>
              </div>
            </div>

            <!-- Boosted amount -->
            <div
              id="boosted_amount"
              class="topup_account"
              v-if="boostPercent > 0 && allOddsAbove13"
            >
              <span class="text"><img :src="icons.fire" alt="Fire Icon" /> Boost Awarded</span>
              <span class="amount">{{ currency }} {{ fmt(boostAmount) }}</span>
            </div>

            <!-- Final payout -->
            <div class="finalpayout-wrap">
              <div class="payout_text-and-finalpayout">
                <div class="payout_text" id="showAddedTax" @click="showTax = !showTax">
                  Final Payout<span class="icon"><i class="bi bi-info-circle"></i></span>
                </div>
                <div class="finalpayout">{{ fmt(finalPayoutAmount) }}</div>
              </div>
            </div>

            <!-- Tax drawer -->
            <div id="addedTax" v-show="showTax">
              <div class="other-details">
                <span class="tax-text">Stake After tax</span>
                <span class="tax-amount"
                  ><span class="currency">{{ currency }}</span>
                  {{ fmt(taxData.stakeAfterTax) }}</span
                >
              </div>
              <div class="other-details">
                <span class="tax-text">WH tax</span>
                <span class="tax-amount"
                  ><span class="currency">{{ currency }}</span>
                  {{ fmt(taxData.withholdingTax) }}</span
                >
              </div>
            </div>

            <!-- Place bet area -->
            <div id="placebetContent">
              <!-- Toggle freebet/balance -->
              <div id="useFreeBet" v-if="bonusStore.hasFreebet">
                <div class="allow_freebet">
                  <div class="toggle-button-cover">
                    <div class="button r" id="button-1">
                      <input
                        type="checkbox"
                        id="usefreebet"
                        class="checkbox"
                        v-model="useFreebet"
                      />
                      <div class="knobs"></div>
                      <div class="layer"></div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Freebet block -->
              <div
                id="useMultiFreeBets"
                class="placebet-wrapper"
                v-if="bonusStore.hasFreebet && useFreebet"
              >
                <div class="placebet__elements">
                  <span class="freebet-elements">
                    <img :src="icons.fireGif" alt="Fire Icon" />
                    <h6>You have a freebet to Use</h6>
                  </span>

                  <div class="amount-wrap">
                    <div id="selectFreebetToUse" v-if="hasMultipleFreebets">
                      <select class="form-select" v-model="selectedFreebetAmount">
                        <option disabled value="0">Select a Freebet</option>
                        <option
                          v-for="bonus in awardedFreebets"
                          :key="bonus.ref_id"
                          :value="parseFloat(bonus.bonus_amount)"
                        >
                          {{ bonus.promo_name }} - {{ currency }}
                          {{ parseFloat(bonus.bonus_amount).toFixed(2) }}
                        </option>
                      </select>
                    </div>

                    <div class="placebet-btn-wrap">
                      <div class="placebet-btn">
                        <button @click="handlePlaceBet" :disabled="placingBet || hasExpiredBets">
                          <span v-if="placingBet" class="spinner-border spinner-border-sm"></span>
                          <template v-else>
                            <span class="text">Freebet</span>
                            <span id="amount">
                              <span class="currency">{{ currency }}</span>
                              <span class="amount">{{ selectedFreebetAmount.toFixed(2) }}</span>
                            </span>
                          </template>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Balance block -->
              <div
                id="useBalance"
                class="placebet-wrapper"
                v-if="!useFreebet || !bonusStore.hasFreebet"
              >
                <div class="placebet__elements">
                  <span class="freebet-elements">
                    <img :src="icons.moneyBag" alt="Money Bag Icon" />
                    <h6>Use Account balance ({{ currency }} {{ fmt(authStore.balance) }})</h6>
                  </span>

                  <div class="choose-amount_btn_wrapper">
                    <small>Choose Amount</small>
                    <div class="choose-amount_btn">
                      <div class="choose-amount">
                        <div class="choose-amount-to-deposit">
                          <button
                            type="button"
                            class="btn"
                            :class="{ open: betSlipStore.stake === 20 }"
                            @click="betSlipStore.setStake(20)"
                          >
                            20
                          </button>
                          <button
                            type="button"
                            class="btn"
                            :class="{ open: betSlipStore.stake === 50 }"
                            @click="betSlipStore.setStake(50)"
                          >
                            50
                          </button>
                          <button
                            type="button"
                            class="btn"
                            :class="{ open: betSlipStore.stake === 100 }"
                            @click="betSlipStore.setStake(100)"
                          >
                            100
                          </button>
                          <button
                            type="button"
                            class="btn"
                            :class="{ open: betSlipStore.stake === 500 }"
                            @click="betSlipStore.setStake(500)"
                          >
                            500
                          </button>
                        </div>
                      </div>
                      <div class="stake-wrapper">
                        <div class="stake-input">
                          <input
                            type="number"
                            id="stakeAmount"
                            class="form-control"
                            placeholder="100"
                            v-model.number="betSlipStore.stake"
                            inputmode="decimal"
                            min="1"
                            max="20000"
                            step="1"
                            @input="validateStake"
                          />
                        </div>
                      </div>
                    </div>
                    <small
                      >Bet Limits: Min Stake 1 {{ currency }}, Max Stake: 50,000
                      {{ currency }}</small
                    >
                  </div>

                  <div class="sharebet-and-placebet_btn">
                    <div
                      id="placeBet"
                      :class="hasExpiredBets ? 'placebet_btn_danger' : 'placebet_btn'"
                    >
                      <!-- REMOVE EXPIRED -->
                      <a
                        v-if="hasExpiredBets"
                        href="#"
                        class="btn btn-danger"
                        @click.prevent="onRemoveExpired"
                      >
                        Remove Expired Matches
                      </a>

                      <!-- PLACE BET -->
                      <a
                        v-else
                        href="#"
                        class="btn btn-primary"
                        @click.prevent="handlePlaceBet"
                        :disabled="placingBet"
                      >
                        <span v-if="placingBet" class="spinner-border spinner-border-sm"></span>
                        <span v-else>Place Bet</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- /#placebetContent -->
          </div>
          <!-- /.placebet__details__wrapper -->
        </div>
        <!-- /.stake-container -->
      </div>
    </div>
    <!--End Of BetSlip -->

    <!-- Top Up Modal -->
    <TopUpModal
      :is-open="isTopUpModalOpen"
      :available-balance="authStore.balance"
      :needed-amount="requiredTopUpAmount"
      @close="handleTopUpClose"
      @deposit-success="handleTopUpSuccess"
    />
</template>

<script setup>
import { ref, computed, watch, watchEffect, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { CDN } from '@/config/cdn'
import { useBetSlipStore } from '@/stores/betSlipStore'
import { useAuthStore } from '@/stores/auth'
import { useBonusStore } from '@/stores/useBonusStore'
import { usePlaceBet } from '@/composables/usePlaceBet'
import { isFixtureExpired, formatDate } from '@/utils/expiry'
import TopUpModal from '@/components/TopUpModal.vue'

/* ---------------- Stores ---------------- */
const router = useRouter()
const betSlipStore = useBetSlipStore()
const authStore = useAuthStore()
const bonusStore = useBonusStore()
const { loading: placingBet, errorMatchId, placeBet, loadBetslip } = usePlaceBet()

/* ---------------- Icons via CDN helper ---------------- */
const icons = {
  nobetsFound: CDN.images('nobets_found.png'),
  boosted: CDN.icons('boosted.png'),
  fire: CDN.icons('fire.png'),
  fireGif: CDN.icons('fire.gif'),
  moneyBag: CDN.icons('money-bag.svg'),
}

/* ---------------- Local UI state ---------------- */
const bookingCodeInput = ref('')
const loadingBookingCode = ref(false)
const showTax = ref(false)
const useFreebet = ref(false)
const selectedFreebetAmount = ref(0)
const isTopUpModalOpen = ref(false)
const requiredTopUpAmount = ref(0)

/* ---------------- Fetch bonuses on mount ---------------- */
onMounted(async () => {
  if (authStore.isAuthenticated) {
    await bonusStore.fetchBonuses()
  }
})

/* ---------------- Freebet logic ---------------- */
const awardedFreebets = computed(() => bonusStore.awardedBonuses)

const hasOneFreebet = computed(() => awardedFreebets.value.length === 1)
const hasMultipleFreebets = computed(() => awardedFreebets.value.length > 1)

// Auto-open freebet toggle if freebets are available
watch(
  () => bonusStore.hasFreebet,
  (has) => {
    if (has) {
      useFreebet.value = true
    }
  },
)

// Handle updates when bonuses change
watchEffect(() => {
  if (!bonusStore.hasFreebet) {
    useFreebet.value = false
    selectedFreebetAmount.value = 0
    return
  }

  const firstBonus = awardedFreebets.value[0]
  if (firstBonus) {
    const parsedAmount = parseFloat(firstBonus.bonus_amount)
    if (hasOneFreebet.value || (selectedFreebetAmount.value === 0 && hasMultipleFreebets.value)) {
      selectedFreebetAmount.value = parsedAmount
    }
  }
})

/* ---------------- Boost calculations ---------------- */
const BOOST_TABLE = [
  { legs: 4, bonus: 4 },
  { legs: 5, bonus: 5 },
  { legs: 6, bonus: 6 },
  { legs: 7, bonus: 8 },
  { legs: 8, bonus: 10 },
  { legs: 9, bonus: 15 },
  { legs: 10, bonus: 18 },
  { legs: 11, bonus: 30 },
  { legs: 12, bonus: 35 },
  { legs: 13, bonus: 40 },
  { legs: 14, bonus: 45 },
  { legs: 15, bonus: 50 },
  { legs: 16, bonus: 55 },
  { legs: 17, bonus: 60 },
  { legs: 18, bonus: 65 },
  { legs: 19, bonus: 70 },
  { legs: 20, bonus: 75 },
  { legs: 21, bonus: 80 },
  { legs: 22, bonus: 85 },
  { legs: 23, bonus: 90 },
  { legs: 24, bonus: 95 },
  { legs: 25, bonus: 100 },
  { legs: 26, bonus: 100 },
]

function getBonusRate(numLegs) {
  if (numLegs < 4) return 0
  let found = 0
  for (const entry of BOOST_TABLE) {
    if (numLegs >= entry.legs) {
      found = entry.bonus
    }
  }
  return found
}

function getNextBonusInfo(numLegs) {
  if (numLegs >= 26) return null
  for (const entry of BOOST_TABLE) {
    if (entry.legs > numLegs) {
      return {
        nextLegsNeeded: entry.legs - numLegs,
        nextBonus: entry.bonus,
      }
    }
  }
  return null
}

const allOddsAbove13 = computed(() => {
  if (!betSlipStore.bets.length) return false
  return betSlipStore.bets.every((bet) => parseFloat(bet.odds) >= 1.3)
})

const boostLegs = computed(() => {
  return betSlipStore.bets.filter((bet) => parseFloat(bet.odds) >= 1.3).length
})

const boostPercent = computed(() => getBonusRate(boostLegs.value))
const nextBoost = computed(() => getNextBonusInfo(boostLegs.value))

/* ---------------- Tax and payout calculations ---------------- */
const WITHHOLDING_TAX_PERCENT = 20

const taxData = computed(() => {
  return betSlipStore.calculateTaxesAndWinnings(
    betSlipStore.stake,
    parseFloat(betSlipStore.totalOdds),
  )
})

const boostAmount = computed(() => {
  if (!taxData.value || boostPercent.value === 0) return 0
  return (taxData.value.payout * boostPercent.value) / 100
})

const MAX_PAYOUT = 1000000

const finalPayoutAmount = computed(() => {
  const boost = boostPercent.value
  const odds = parseFloat(betSlipStore.totalOdds)
  const { stakeAfterTax } = taxData.value

  let calculatedPayout = 0

  if (boost > 0 && allOddsAbove13.value) {
    const grossWin = stakeAfterTax * odds * (1 + boost / 100)
    const profit = grossWin - stakeAfterTax
    const rawWithholding = profit - (profit * 100) / (100 + WITHHOLDING_TAX_PERCENT)
    const net = grossWin - rawWithholding
    calculatedPayout = parseFloat(net.toFixed(2))
  } else {
    calculatedPayout = parseFloat(taxData.value.payout.toFixed(2))
  }

  // Cap the payout at MAX_PAYOUT
  return Math.min(calculatedPayout, MAX_PAYOUT)
})

/* ---------------- Expired bets ---------------- */
const hasExpiredBets = computed(() => betSlipStore.bets.some((bet) => isFixtureExpired(bet)))

const isExpired = (bet) => isFixtureExpired(bet)

/* ---------------- Currency ---------------- */
const currency = computed(() => 'KES')

/* ---------------- Stake validation ---------------- */
const MIN_STAKE = 1
const MAX_STAKE = 20000

function validateStake(event) {
  let value = parseFloat(event.target.value)

  // Prevent negative values
  if (value < 0) {
    value = 0
  }

  // Enforce minimum stake
  if (value > 0 && value < MIN_STAKE) {
    value = MIN_STAKE
  }

  // Enforce maximum stake
  if (value > MAX_STAKE) {
    value = MAX_STAKE
  }

  // Update the store with validated value
  betSlipStore.setStake(value)
}

/* ---------------- Actions ---------------- */
function fmt(n) {
  const num = Number(n || 0)
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function onClose() {
  const drawer = document.getElementById('BetSlipContent')
  drawer?.classList?.remove('open', 'show')
  // Allow scrolling on body when betslip closes on mobile
  document.body.style.overflow = 'auto'
}

function onRemoveAll() {
  betSlipStore.clearBets()
  betSlipStore.clearBookingCode()
  // Reset shared bet state when clearing all bets
  authStore.resetSharedBetState()
  // Close betslip on mobile when all bets removed
  if (window.innerWidth < 768) {
    onClose()
  }
}

function onRemove(outcomeId) {
  betSlipStore.removeBet(outcomeId)
  // Clear booking code if no bets left
  if (!betSlipStore.hasBets) {
    betSlipStore.clearBookingCode()
    // Reset shared bet state when no bets remaining
    authStore.resetSharedBetState()
  }
}

function onRemoveExpired() {
  // Create a copy of bets array to avoid mutation issues during iteration
  const betsToCheck = [...betSlipStore.bets]

  betsToCheck.forEach((bet) => {
    if (isExpired(bet)) {
      betSlipStore.removeBet(bet.outcome_id)
    }
  })

  // Clear booking code if no bets left
  if (!betSlipStore.hasBets) {
    betSlipStore.clearBookingCode()
    // Reset shared bet state when no bets remaining
    authStore.resetSharedBetState()
  }
}

async function handleLoadBookingCode() {
  if (!bookingCodeInput.value) return

  loadingBookingCode.value = true
  const success = await loadBetslip(bookingCodeInput.value)
  loadingBookingCode.value = false

  if (success) {
    bookingCodeInput.value = ''
  }
}

async function handlePlaceBet() {
  const stakeAmount = useFreebet.value ? selectedFreebetAmount.value : betSlipStore.stake
  const result = await placeBet(stakeAmount, useFreebet.value, selectedFreebetAmount.value)

  if (result.success) {
    // Close betslip and redirect to success page
    onClose()
    router.push({ name: 'PlaceBetSuccess' })
  } else if (result.insufficientFunds) {
    // Handle insufficient funds - open topup modal
    requiredTopUpAmount.value = result.requiredAmount
    isTopUpModalOpen.value = true
  }
}

function handleTopUpClose() {
  isTopUpModalOpen.value = false
}

function handleTopUpSuccess() {
  isTopUpModalOpen.value = false
  // Optionally refresh the betslip or other UI elements
}
</script>

<style scoped>
/* ... existing styles ... */
</style>
